/**
 * Auth initialization hook that handles authentication verification
 * outside of component render cycles to prevent infinite loops
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { apiClient, TokenManager } from '../lib/api/api-client';
import type { User } from '../store/authStore';

export const useAuthInit = () => {
  const { isAuthenticated, user, setUser, logout } = useAuthStore();
  const verificationAttempted = useRef(false);
  const isVerifying = useRef(false);

  useEffect(() => {
    const verifyUserData = async () => {
      if (
        isAuthenticated && 
        !user && 
        !verificationAttempted.current && 
        !isVerifying.current
      ) {
        verificationAttempted.current = true;
        isVerifying.current = true;

        try {
          const response = await apiClient.verifyToken();
          
          if (response?.data?.data) {
            const verificationResult = response.data.data;
            
            if (verificationResult.status === 'VALID') {
              const userData: User = {
                id: verificationResult.username || 'admin',
                email: verificationResult.username || '',
                role: (verificationResult.roles?.[0] || 'ADMIN') as 'ADMIN' | 'OPERATOR' | 'USER',
                emailVerified: true,
              };
              
              setUser(userData);
            } else {
              logout();
            }
          } else {
            logout();
          }
        } catch (error) {
          logout();
        } finally {
          isVerifying.current = false;
        }
      }
    };

    if (isAuthenticated && !user && !verificationAttempted.current && !isVerifying.current) {
      console.log('⚠️ Auth state incomplete - authenticated but missing user data');
      verifyUserData();
    } else if (isAuthenticated && user) {
      console.log('✅ Auth state complete - authenticated with user data');
    } else if (!isAuthenticated) {
      console.log('❌ Not authenticated');
    }
  }, [isAuthenticated, !!user, setUser, logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      verificationAttempted.current = false;
      isVerifying.current = false;
      console.log('🔄 Auth verification flags reset');
    }
  }, [isAuthenticated]);
};
